import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "title" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "collapse-button" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronRight" })
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronRight" }, null, -1)
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronLeft" }, null, -1)
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = { class: "title" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "collapse-button" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronLeft" })
], -1)
const _hoisted_9 = { class: "title" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("div", { class: "collapse-button" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronBottom" })
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronBottom" }, null, -1)
const _hoisted_12 = [
  _hoisted_11
]
const _hoisted_13 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronTop" }, null, -1)
const _hoisted_14 = [
  _hoisted_13
]
const _hoisted_15 = { class: "title" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { class: "collapse-button" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-ChevronTop" })
], -1)

import Split from "split.js";
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { TranslateResult } from "vue-i18n";

//#region General
type AreaOptions = {
  title?: string | TranslateResult;
  allowCollapse?: boolean;
  startCollapsed?: boolean;
  isVisible?: boolean;
  classes?: string | string[] | Record<string, boolean>;
};

type ViewModel = {
  collapsed: { left: boolean; right: boolean; top: boolean; bottom: boolean };
  isVisible: { left: boolean; right: boolean; top: boolean; bottom: boolean };
};

export default /*#__PURE__*/_defineComponent({
  __name: 'GridSplitter',
  props: {
    isLeftCollapsed: { type: Boolean },
    isRightCollapsed: { type: Boolean },
    isTopCollapsed: { type: Boolean },
    isBottomCollapsed: { type: Boolean },
    isLeftVisible: { type: Boolean },
    isRightVisible: { type: Boolean },
    isTopVisible: { type: Boolean },
    isBottomVisible: { type: Boolean },
    topOptions: {},
    bottomOptions: {},
    leftOptions: {},
    rightOptions: {},
    vertical: { type: Boolean },
    splitterOptions: {},
    isGutterTransparent: { type: Boolean }
  },
  emits: [
  "update:isLeftCollapsed",
  "update:isRightCollapsed",
  "update:isTopCollapsed",
  "update:isBottomCollapsed",
  "update:isLeftVisible",
  "update:isRightVisible",
  "update:isTopVisible",
  "update:isBottomVisible"
],
  setup(__props: any, { expose: __expose, emit: emits }) {

const props = __props;

const gridSplitter = ref<HTMLElement>();
const leftArea = ref<HTMLElement>();
const rightArea = ref<HTMLElement>();
const topArea = ref<HTMLElement>();
const bottomArea = ref<HTMLElement>();





let _split: Split.Instance | undefined;

let splitOptions = reactive<Split.Options>({
  sizes: [50, 50],
  minSize: 350,
  gutterSize: 10
});

const areaStyle = computed(() => {
  if (splitOptions && splitOptions.minSize) {
    return props.vertical ? { minHeight: `${splitOptions.minSize}px` } : { minWidth: `${splitOptions.minSize}px` };
  }
  return {};
});
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  collapsed: {
    left: props.leftOptions?.startCollapsed ?? false,
    right: props.rightOptions?.startCollapsed ?? false,
    top: props.topOptions?.startCollapsed ?? false,
    bottom: props.bottomOptions?.startCollapsed ?? false
  },
  isVisible: {
    left: props.leftOptions?.isVisible ?? true,
    right: props.rightOptions?.isVisible ?? true,
    top: props.topOptions?.isVisible ?? true,
    bottom: props.bottomOptions?.isVisible ?? true
  }
});
//#endregion

//#region Splitter

const createSplitter = () => {
  if (props.splitterOptions) splitOptions = Object.assign(splitOptions, props.splitterOptions);

  if (!props.vertical && leftArea?.value && vm.isVisible.left && rightArea?.value && vm.isVisible.right && !(vm.collapsed.left || vm.collapsed.right)) {
    _split = Split([leftArea.value, rightArea.value], splitOptions);
  }

  if (props.vertical && topArea?.value && vm.isVisible.top && bottomArea?.value && vm.isVisible.bottom && !(vm.collapsed.top || vm.collapsed.bottom)) {
    Object.assign(splitOptions, { direction: "vertical" });
    _split = Split([topArea.value, bottomArea.value], splitOptions);
  }

  if (props.isGutterTransparent) {
    gridSplitter.value?.querySelector(".gutter")?.classList.add("transparent");
  }
};

const destroySplitter = () => {
  if (_split) {
    _split.destroy();
    _split = undefined;
  }
};

watch(
  () => props.splitterOptions,
  () => {
    destroySplitter();
    createSplitter();
  },
  { deep: true }
);

//#endregion

//#region Collapse

watch(
  vm.collapsed,
  (nv) => {
    if (nv.left || nv.right || nv.top || nv.bottom) {
      destroySplitter();
    } else {
      createSplitter();
    }
    emits("update:isLeftCollapsed", nv.left);
    emits("update:isRightCollapsed", nv.right);
    emits("update:isTopCollapsed", nv.top);
    emits("update:isBottomCollapsed", nv.bottom);
  },
  { deep: true }
);

watch(
  vm.isVisible,
  (nv) => {
    if (!nv.left || !nv.right || !nv.top || !nv.bottom) {
      destroySplitter();
    } else {
      createSplitter();
    }
    emits("update:isLeftVisible", nv.left);
    emits("update:isRightVisible", nv.right);
    emits("update:isTopVisible", nv.top);
    emits("update:isBottomVisible", nv.bottom);
  },
  { deep: true }
);

watch(
  () => props.isLeftCollapsed,
  (nv) => {
    vm.collapsed.left = nv;
  }
);
watch(
  () => props.isRightCollapsed,
  (nv) => {
    vm.collapsed.right = nv;
  }
);
watch(
  () => props.isTopCollapsed,
  (nv) => {
    vm.collapsed.top = nv;
  }
);
watch(
  () => props.isBottomCollapsed,
  (nv) => {
    vm.collapsed.bottom = nv;
  }
);
watch(
  () => props.isLeftVisible,
  (nv) => {
    vm.isVisible.left = nv;
  }
);
watch(
  () => props.isRightVisible,
  (nv) => {
    vm.isVisible.right = nv;
  }
);
watch(
  () => props.isTopVisible,
  (nv) => {
    vm.isVisible.top = nv;
  }
);
watch(
  () => props.isBottomVisible,
  (nv) => {
    vm.isVisible.bottom = nv;
  }
);

const collapse = (region: string, collapse = true) => {
  switch (region) {
    case "left":
      vm.collapsed.left = collapse;
      break;
    case "right":
      vm.collapsed.right = collapse;
      break;
    case "top":
      vm.collapsed.top = collapse;
      break;
    case "bottom":
      vm.collapsed.bottom = collapse;
      break;
  }
};

const isVisible = (region: string, visible = false) => {
  switch (region) {
    case "left":
      vm.isVisible.left = visible;
      break;
    case "right":
      vm.isVisible.right = visible;
      break;
    case "top":
      vm.isVisible.top = visible;
      break;
    case "bottom":
      vm.isVisible.bottom = visible;
      break;
  }
};
//#endregion

//#region Life Cycle
onMounted(() => {
  createSplitter();
});

onBeforeUnmount(() => {
  destroySplitter();
});
//#endregion

//#region Exposed
__expose({ collapse, isVisible });
//#endregion

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["grid-splitter", { 'flex-column': _ctx.vertical }]),
    ref_key: "gridSplitter",
    ref: gridSplitter
  }, [
    (!_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "collapsed-area-left",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (vm.collapsed.left = false))
        }, [
          _createElementVNode("div", _hoisted_1, _toDisplayString(_ctx.leftOptions?.title), 1),
          _hoisted_2
        ], 512)), [
          [_vShow, vm.collapsed.left && vm.isVisible.left]
        ])
      : _createCommentVNode("", true),
    (!_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["leftArea", _ctx.leftOptions?.classes]),
          ref_key: "leftArea",
          ref: leftArea,
          style: _normalizeStyle(areaStyle.value)
        }, [
          (_ctx.rightOptions?.allowCollapse)
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "right-collapse",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (vm.collapsed.right = true))
              }, _hoisted_4, 512)), [
                [_vShow, !vm.collapsed.right]
              ])
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "left")
        ], 6)), [
          [_vShow, !vm.collapsed.left && vm.isVisible.left]
        ])
      : _createCommentVNode("", true),
    (!_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 2,
          class: _normalizeClass(["rightArea", _ctx.rightOptions?.classes]),
          ref_key: "rightArea",
          ref: rightArea,
          style: _normalizeStyle(areaStyle.value)
        }, [
          (_ctx.leftOptions?.allowCollapse)
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "left-collapse",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (vm.collapsed.left = true))
              }, _hoisted_6, 512)), [
                [_vShow, !vm.collapsed.left]
              ])
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "right")
        ], 6)), [
          [_vShow, !vm.collapsed.right && vm.isVisible.right]
        ])
      : _createCommentVNode("", true),
    (!_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 3,
          class: "collapsed-area-right",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (vm.collapsed.right = false))
        }, [
          _createElementVNode("div", _hoisted_7, _toDisplayString(_ctx.rightOptions?.title), 1),
          _hoisted_8
        ], 512)), [
          [_vShow, vm.collapsed.right && vm.isVisible.right]
        ])
      : _createCommentVNode("", true),
    (_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 4,
          class: "collapsed-area-top",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (vm.collapsed.top = false))
        }, [
          _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.topOptions?.title), 1),
          _hoisted_10
        ], 512)), [
          [_vShow, vm.collapsed.top && vm.isVisible.top]
        ])
      : _createCommentVNode("", true),
    (_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 5,
          class: _normalizeClass(["topArea", _ctx.topOptions?.classes]),
          ref_key: "topArea",
          ref: topArea,
          style: _normalizeStyle(areaStyle.value)
        }, [
          (_ctx.bottomOptions?.allowCollapse)
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "bottom-collapse",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (vm.collapsed.bottom = true))
              }, _hoisted_12, 512)), [
                [_vShow, !vm.collapsed.bottom]
              ])
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "top")
        ], 6)), [
          [_vShow, !vm.collapsed.top && vm.isVisible.top]
        ])
      : _createCommentVNode("", true),
    (_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 6,
          class: _normalizeClass(["bottomArea", _ctx.bottomOptions?.classes]),
          ref_key: "bottomArea",
          ref: bottomArea,
          style: _normalizeStyle(areaStyle.value)
        }, [
          (_ctx.topOptions?.allowCollapse)
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "top-collapse",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (vm.collapsed.top = true))
              }, _hoisted_14, 512)), [
                [_vShow, !vm.collapsed.top]
              ])
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "bottom")
        ], 6)), [
          [_vShow, !vm.collapsed.bottom && vm.isVisible.bottom]
        ])
      : _createCommentVNode("", true),
    (_ctx.vertical)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 7,
          class: "collapsed-area-bottom",
          onClick: _cache[7] || (_cache[7] = ($event: any) => (vm.collapsed.bottom = false))
        }, [
          _createElementVNode("div", _hoisted_15, _toDisplayString(_ctx.bottomOptions?.title), 1),
          _hoisted_16
        ], 512)), [
          [_vShow, vm.collapsed.bottom && vm.isVisible.bottom]
        ])
      : _createCommentVNode("", true)
  ], 2))
}
}

})